import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '../contexts/LanguageContext'
import { useTeamMembers } from '../hooks/useAirtable'
import { sanitizeRichText } from '../utils/sanitize'

const MembersSection = ({ onOpenChat }) => {
  const [selectedMember, setSelectedMember] = useState(null)
  const { t } = useTranslation()
  const { currentLanguage } = useLanguage()
  
  // Fetch team members from Airtable
  const { teamMembers: members, loading, error } = useTeamMembers(true, currentLanguage)
  
  // Debug logging
  React.useEffect(() => {
    console.log('👥 Team members loaded:', {
      count: members.length,
      language: currentLanguage,
      members: members.map(m => ({
        id: m.id,
        name: m.name,
        position: m.position,
        specialization: m.specialization,
        active: m.active,
        hasPosition: !!m.position,
        positionLength: m.position ? m.position.length : 0
      }))
    });
    
    if (members.length > 0) {
      console.log('📝 First member detailed data:', {
        fullMember: members[0],
        keys: Object.keys(members[0])
      });
    }
  }, [members, currentLanguage]);

  // Enhanced loading state with member card skeletons
  if (loading) {
    return (
      <section style={{ padding: '80px 0' }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "24px", maxWidth: "1200px", margin: "0 auto", padding: "0 20px" }}>
          {[...Array(4)].map((_, i) => (
            <div key={i} style={{
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              textAlign: "center",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)"
            }}>
              {/* Avatar skeleton */}
              <div 
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  margin: "0 auto 16px auto",
                  background: "linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 2s infinite",
                  animationDelay: `${i * 0.1}s`
                }}
              />
              
              {/* Name skeleton */}
              <div 
                style={{
                  height: "20px",
                  width: "60%",
                  margin: "0 auto 8px auto",
                  borderRadius: "4px",
                  background: "linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 2s infinite",
                  animationDelay: `${i * 0.1}s`
                }}
              />
              
              {/* Title skeleton */}
              <div 
                style={{
                  height: "16px",
                  width: "80%",
                  margin: "0 auto",
                  borderRadius: "4px",
                  background: "linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 2s infinite",
                  animationDelay: `${i * 0.1}s`
                }}
              />
            </div>
          ))}
        </div>
        <style>{`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}</style>
      </section>
    )
  }

  // Show error state
  if (error || !members || members.length === 0) {
    return (
      <section style={{ padding: '80px 0', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>
          {t('common.error')} - Unable to load team members
        </div>
      </section>
    )
  }


  const handleMemberClick = (memberId) => {
    console.log('🔍 Member clicked:', memberId, 'Available members:', members.length)
    setSelectedMember(memberId)
  }

  const handleBackToMembers = () => {
    setSelectedMember(null)
  }

  return (
    <>
      {/* Full Profile Overlay */}
      {selectedMember && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(25px)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(30px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 32px 80px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            width: '100%',
            maxWidth: '950px',
            maxHeight: '90vh',
            overflow: 'hidden',
            position: 'relative',
            animation: 'slideInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Corner Close Button */}
            <button
              onClick={handleBackToMembers}
              className="modal-close-button"
              style={{
                position: 'fixed',
                top: '16px',
                right: '16px',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                border: '2px solid rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                zIndex: 999999,
                backdropFilter: 'blur(20px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.9)',
                color: 'rgba(0, 0, 0, 0.8)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 1)'
                e.target.style.transform = 'scale(1.1)'
                e.target.style.borderColor = 'rgba(0, 0, 0, 0.2)'
                e.target.style.boxShadow = '0 12px 48px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 1)'
                e.target.style.color = 'rgba(0, 0, 0, 0.9)'
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.95)'
                e.target.style.transform = 'scale(1)'
                e.target.style.borderColor = 'rgba(0, 0, 0, 0.1)'
                e.target.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.8)'
                e.target.style.color = 'rgba(0, 0, 0, 0.7)'
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>

            {/* Profile Content */}
            {(() => {
              const member = members.find(m => m.id === selectedMember)
              if (!member) {
                console.warn('⚠️ Selected member not found:', selectedMember, 'Available members:', members.map(m => m.id))
                return null
              }
              
              // Debug the selected member data
              console.log('🔍 Selected member data:', {
                id: member.id,
                name: member.name,
                position: member.position,
                bio: member.bio,
                education: member.education,
                experience: member.experience,
                expertise: member.expertise,
                languages: member.languages,
                hasEducation: member.education && member.education.length > 0,
                hasExperience: member.experience && member.experience.length > 0,
                hasExpertise: member.expertise && member.expertise.length > 0,
                hasLanguages: member.languages && member.languages.length > 0
              });
              
              return (
                <div className="profile-modal" style={{ 
                  flex: 1,
                  overflowY: 'auto',
                  padding: '0'
                }}>
                  {/* Content Container */}
                  <div style={{ padding: '48px' }}>
                  {/* Modern Header Section */}
                  <div className="profile-header" style={{
                    display: 'flex',
                    gap: '32px',
                    marginBottom: '48px',
                    alignItems: 'center',
                    padding: '32px',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(248, 250, 252, 0.9))',
                    borderRadius: '20px',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04)'
                  }}>
                    <div className="profile-image" style={{
                      width: '160px',
                      height: '160px',
                      borderRadius: '24px',
                      backgroundImage: `url('${member.image}')`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      boxShadow: '0 16px 48px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.8)',
                      flexShrink: 0,
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      {/* Image Overlay */}
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.1))',
                        borderRadius: '24px'
                      }} />
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <h2 style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 'clamp(28px, 4vw, 40px)',
                        fontWeight: 700,
                        color: 'rgb(15, 23, 42)',
                        margin: '0 0 8px 0',
                        letterSpacing: '-0.025em',
                        lineHeight: '1.2'
                      }}>
                        {member.name}
                      </h2>
                      
                      <p style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '18px',
                        fontWeight: 600,
                        color: 'rgba(15, 23, 42, 0.8)',
                        margin: '0 0 12px 0',
                        lineHeight: '1.4'
                      }}>
                        {member.position}
                      </p>
                      
                      {member.specialization && (
                        <div style={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '16px',
                          fontWeight: 500,
                          color: 'rgba(15, 23, 42, 0.6)',
                          margin: '0',
                          lineHeight: '1.4'
                        }}>
                          {typeof member.specialization === 'string' && member.specialization.includes('<') ? (
                            <div dangerouslySetInnerHTML={{ __html: sanitizeRichText(member.specialization) }} />
                          ) : (
                            <p>{typeof member.specialization === 'string' 
                              ? member.specialization.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').trim()
                              : member.specialization}</p>
                          )}
                        </div>
                      )}
                      {/* Display specialization as tags if it's a list */}
                      {member.expertise && member.expertise.length > 0 && member.expertise.length <= 5 && (
                        <div style={{ 
                          display: 'flex', 
                          gap: '6px', 
                          flexWrap: 'wrap',
                          marginTop: '8px'
                        }}>
                          {member.expertise.slice(0, 3).map((skill, index) => (
                            <span key={index} style={{
                              padding: '4px 10px',
                              background: 'rgba(0, 0, 0, 0.05)',
                              border: '1px solid rgba(0, 0, 0, 0.1)',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: 500,
                              color: 'rgba(0, 0, 0, 0.6)'
                            }}>
                              {skill}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Simple Bio Section - Rich Text */}
                  {member.bio && (
                    <div style={{ 
                      marginBottom: '32px',
                      padding: '24px',
                      background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.6), rgba(241, 245, 249, 0.8))',
                      borderRadius: '16px',
                      border: '1px solid rgba(0, 0, 0, 0.04)'
                    }}>
                      <div 
                        style={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '16px',
                          lineHeight: '1.6',
                          color: 'rgba(15, 23, 42, 0.8)',
                          textAlign: currentLanguage === 'ar' ? 'right' : 'left'
                        }}
                        dangerouslySetInnerHTML={{ __html: sanitizeRichText(member.bio) }}
                      />
                    </div>
                  )}

                  {/* Professional Details Grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '20px',
                    marginBottom: '32px'
                  }}>
                    {/* Education - Rich Text */}
                    {member.education && (
                      <div style={{
                        padding: '20px',
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.7))',
                        borderRadius: '12px',
                        border: '1px solid rgba(0, 0, 0, 0.06)'
                      }}>
                        <h4 style={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '16px',
                          fontWeight: 700,
                          color: 'rgb(15, 23, 42)',
                          margin: '0 0 12px 0'
                        }}>
                          {currentLanguage === 'ar' ? 'التعليم' : 'Education'}
                        </h4>
                        {typeof member.education === 'string' ? (
                          <div 
                            style={{
                              fontSize: '14px',
                              lineHeight: '1.6',
                              color: 'rgba(15, 23, 42, 0.8)',
                              textAlign: currentLanguage === 'ar' ? 'right' : 'left'
                            }}
                            dangerouslySetInnerHTML={{ __html: sanitizeRichText(member.education) }}
                          />
                        ) : (
                          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {member.education_list && member.education_list.length > 0 ? (
                              member.education_list.map((edu, index) => (
                                <li key={index} style={{
                                  fontSize: '14px',
                                  lineHeight: '1.5',
                                  color: 'rgba(15, 23, 42, 0.8)',
                                  marginBottom: '8px',
                                  paddingLeft: '16px',
                                  position: 'relative'
                                }}>
                                  <span style={{ 
                                    position: 'absolute', 
                                    left: '0', 
                                    top: '6px',
                                    width: '4px',
                                    height: '4px',
                                    borderRadius: '50%',
                                    background: 'rgba(0, 0, 0, 0.4)'
                                  }}></span>
                                  {edu}
                                </li>
                              ))
                            ) : (
                              member.education.map((edu, index) => (
                                <li key={index} style={{
                                  fontSize: '14px',
                                  lineHeight: '1.5',
                                  color: 'rgba(15, 23, 42, 0.8)',
                                  marginBottom: '8px',
                                  paddingLeft: '16px',
                                  position: 'relative'
                                }}>
                                  <span style={{ 
                                    position: 'absolute', 
                                    left: '0', 
                                    top: '6px',
                                    width: '4px',
                                    height: '4px',
                                    borderRadius: '50%',
                                    background: 'rgba(0, 0, 0, 0.4)'
                                  }}></span>
                                  {edu}
                                </li>
                              ))
                            )}
                          </ul>
                        )}
                      </div>
                    )}

                    {/* Experience - Rich Text */}
                    {member.experience && (
                      <div style={{
                        padding: '20px',
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.7))',
                        borderRadius: '12px',
                        border: '1px solid rgba(0, 0, 0, 0.06)'
                      }}>
                        <h4 style={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '16px',
                          fontWeight: 700,
                          color: 'rgb(15, 23, 42)',
                          margin: '0 0 12px 0'
                        }}>
                          {currentLanguage === 'ar' ? 'الخبرة' : 'Experience'}
                        </h4>
                        {typeof member.experience === 'string' ? (
                          <div 
                            style={{
                              fontSize: '14px',
                              lineHeight: '1.6',
                              color: 'rgba(15, 23, 42, 0.8)',
                              textAlign: currentLanguage === 'ar' ? 'right' : 'left'
                            }}
                            dangerouslySetInnerHTML={{ __html: sanitizeRichText(member.experience) }}
                          />
                        ) : (
                          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {member.experience_list && member.experience_list.length > 0 ? (
                              member.experience_list.map((exp, index) => (
                                <li key={index} style={{
                                  fontSize: '14px',
                                  lineHeight: '1.5',
                                  color: 'rgba(15, 23, 42, 0.8)',
                                  marginBottom: '8px',
                                  paddingLeft: '16px',
                                  position: 'relative'
                                }}>
                                  <span style={{ 
                                    position: 'absolute', 
                                    left: '0', 
                                    top: '6px',
                                    width: '4px',
                                    height: '4px',
                                    borderRadius: '50%',
                                    background: 'rgba(0, 0, 0, 0.4)'
                                  }}></span>
                                  {exp}
                                </li>
                              ))
                            ) : (
                              member.experience.map((exp, index) => (
                                <li key={index} style={{
                                  fontSize: '14px',
                                  lineHeight: '1.5',
                                  color: 'rgba(15, 23, 42, 0.8)',
                                  marginBottom: '8px',
                                  paddingLeft: '16px',
                                  position: 'relative'
                                }}>
                                  <span style={{ 
                                    position: 'absolute', 
                                    left: '0', 
                                    top: '6px',
                                    width: '4px',
                                    height: '4px',
                                    borderRadius: '50%',
                                    background: 'rgba(0, 0, 0, 0.4)'
                                  }}></span>
                                  {exp}
                                </li>
                              ))
                            )}
                          </ul>
                        )}
                      </div>
                    )}

                    {/* Areas of Expertise */}
                    {member.expertise && member.expertise.length > 0 && (
                      <div style={{
                        padding: '20px',
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.7))',
                        borderRadius: '12px',
                        border: '1px solid rgba(0, 0, 0, 0.06)'
                      }}>
                        <h4 style={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '16px',
                          fontWeight: 700,
                          color: 'rgb(15, 23, 42)',
                          margin: '0 0 12px 0'
                        }}>
                          {currentLanguage === 'ar' ? 'مجالات الخبرة' : 'Areas of Expertise'}
                        </h4>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {member.expertise.map((skill, index) => (
                            <span key={index} style={{
                              padding: '6px 12px',
                              background: 'rgba(0, 0, 0, 0.05)',
                              border: '1px solid rgba(0, 0, 0, 0.1)',
                              borderRadius: '8px',
                              fontSize: '12px',
                              fontWeight: 600,
                              color: 'rgba(0, 0, 0, 0.7)'
                            }}>
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Languages */}
                    {member.languages && member.languages.length > 0 && (
                      <div style={{
                        padding: '20px',
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.7))',
                        borderRadius: '12px',
                        border: '1px solid rgba(0, 0, 0, 0.06)'
                      }}>
                        <h4 style={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '16px',
                          fontWeight: 700,
                          color: 'rgb(15, 23, 42)',
                          margin: '0 0 12px 0'
                        }}>
                          {currentLanguage === 'ar' ? 'اللغات' : 'Languages'}
                        </h4>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {member.languages.map((lang, index) => (
                            <span key={index} style={{
                              padding: '8px 14px',
                              background: 'rgba(0, 0, 0, 0.05)',
                              border: '1px solid rgba(0, 0, 0, 0.1)',
                              borderRadius: '10px',
                              fontSize: '13px',
                              fontWeight: 600,
                              color: 'rgba(0, 0, 0, 0.7)'
                            }}>
                              {lang}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Contact Information */}
                  {(member.email || member.phone) && (
                    <div style={{ 
                      marginBottom: '32px',
                      padding: '20px',
                      background: 'rgba(0, 0, 0, 0.02)',
                      borderRadius: '12px',
                      border: '1px solid rgba(0, 0, 0, 0.05)'
                    }}>
                      <h4 style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '16px',
                        fontWeight: 700,
                        color: 'rgb(15, 23, 42)',
                        margin: '0 0 12px 0'
                      }}>
                        {currentLanguage === 'ar' ? 'معلومات الاتصال' : 'Contact Information'}
                      </h4>
                      {member.email && (
                        <p style={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '14px',
                          color: 'rgba(15, 23, 42, 0.7)',
                          margin: '0 0 8px 0'
                        }}>
                          📧 {member.email}
                        </p>
                      )}
                      {member.phone && (
                        <p style={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '14px',
                          color: 'rgba(15, 23, 42, 0.7)',
                          margin: '0'
                        }}>
                          📱 {member.phone}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Modern Action Button */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: '32px',
                    marginTop: '32px',
                    borderTop: '1px solid rgba(0, 0, 0, 0.06)'
                  }}>
                    <button
                      onClick={() => {
                        if (onOpenChat) {
                          onOpenChat()
                          handleBackToMembers()
                        }
                      }}
                      style={{
                        padding: '16px 40px',
                        background: 'linear-gradient(135deg, rgb(15, 23, 42), rgb(30, 41, 59))',
                        border: 'none',
                        borderRadius: '16px',
                        color: 'white',
                        fontSize: '15px',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: '600',
                        letterSpacing: '-0.01em',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        userSelect: 'none',
                        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px) scale(1.02)'
                        e.target.style.boxShadow = '0 12px 32px rgba(15, 23, 42, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2)'
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0) scale(1)'
                        e.target.style.boxShadow = '0 8px 24px rgba(15, 23, 42, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      <span style={{ position: 'relative', zIndex: 2 }}>
                        Consult with {member.name.split(' ')[0]}
                      </span>
                      {/* Button glow effect */}
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                        borderRadius: '16px',
                        opacity: 0,
                        transition: 'opacity 0.3s ease'
                      }} />
                    </button>
                  </div>
                  </div>
                </div>
              )
            })()}
          </div>
        </div>
      )}

      {/* Members Section */}
      <section
        style={{
          display: "block",
          padding: "clamp(80px, 12vh, 120px) 0px clamp(80px, 12vh, 120px) 0px",
          margin: "0px",
          width: "100%",
          position: "relative",
          background: "transparent",
          maxWidth: "100%",
        }}
      >
        <div
          className="container container--large"
          style={{
            color: "rgb(26, 26, 26)",
            width: "100%",
            boxSizing: "border-box",
            maxWidth: "1200px",
            margin: "0px auto",
            padding: "0 clamp(15px, 4vw, 20px)",
          }}
        >
          {/* Header */}
          <div style={{
            textAlign: "center",
            marginBottom: "60px",
          }}>
            <h2 style={{
              fontFamily: "ff-real-text-pro, sans-serif",
              fontStyle: "normal",
              fontWeight: 300,
              letterSpacing: "-0.025em",
              lineHeight: "1.3em",
              margin: "0px 0px 15px 0px",
              fontSize: "clamp(30px, 5vw, 50px)",
              color: "rgb(26, 26, 26)",
            }}>
              {t('team.title', { defaultValue: currentLanguage === 'ar' ? 'فريقنا القانوني' : 'Our Legal Team' })}
            </h2>
            <p style={{
              fontFamily: "Merriweather",
              fontSize: "18px",
              fontStyle: "normal",
              fontWeight: 300,
              lineHeight: "1.66em",
              marginTop: "0px",
              marginBottom: "0px",
              color: "rgb(26, 26, 26)",
              maxWidth: "600px",
              margin: "0 auto"
            }}>
              {t('team.subtitle', { defaultValue: currentLanguage === 'ar' ? 'محترفون ذوو خبرة مكرسون لنجاحك' : 'Experienced professionals dedicated to your success' })}
            </p>
          </div>

          {/* Members Grid */}
          <div 
            className="members-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "20px",
              marginBottom: "40px"
            }}>
            {members.map((member, index) => (
              <div
                key={member.id}
                className="member-card"
                style={{
                  position: "relative",
                  borderRadius: "16px",
                  overflow: "hidden",
                  height: "280px",
                  backgroundImage: `url('${member.image}')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  cursor: "pointer",
                  transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                  animation: `slideInUp 0.6s ease-out ${index * 0.1}s both`
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-10px) scale(1.02)'
                  e.target.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.2)'
                  const overlay = e.target.querySelector('.member-overlay')
                  if (overlay) {
                    overlay.style.opacity = '1'
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0) scale(1)'
                  e.target.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.1)'
                  const overlay = e.target.querySelector('.member-overlay')
                  if (overlay) {
                    overlay.style.opacity = '0'
                  }
                }}
                onClick={() => handleMemberClick(member.id)}
              >
                {/* Foreground image - on top of background */}
                {member.image && (
                  <img 
                    src={member.image} 
                    alt={member.name}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      zIndex: 2,
                      pointerEvents: 'none'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none'
                    }}
                  />
                )}

                {/* Dark overlay for text readability */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.7))',
                  zIndex: 1
                }} />

                {/* Member info always visible */}
                <div style={{
                  position: 'absolute',
                  bottom: '15px',
                  left: '15px',
                  right: '15px',
                  zIndex: 3
                }}>
                  <h3 
                    className="member-name"
                    style={{
                      margin: "0px 0px 4px 0px",
                      fontFamily: "Inter, sans-serif",
                      fontSize: "clamp(16px, 2.5vw, 20px)",
                      fontWeight: 600,
                      lineHeight: "1.2em",
                      color: "white",
                      letterSpacing: "-0.025em",
                      textShadow: "0 2px 8px rgba(0, 0, 0, 0.8)",
                      transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                      transform: "translateY(0)",
                      opacity: 1
                    }}
                  >
                    {member.name}
                  </h3>
                  <p 
                    className="member-position"
                    style={{
                      margin: "0px 0px 8px 0px",
                      fontFamily: "Inter, sans-serif",
                      fontSize: "clamp(14px, 2vw, 16px)",
                      fontWeight: 600,
                      color: "rgba(255, 255, 255, 0.95)",
                      textShadow: "0 2px 8px rgba(0, 0, 0, 0.8)",
                      transition: "all 0.4s ease",
                      transform: "translateY(0)",
                      opacity: 1,
                      lineHeight: "1.2"
                    }}
                  >
                    {member.position || 'منصب غير محدد'}
                  </p>
                </div>

                {/* Hover overlay with expanded info */}
                <div 
                  className="member-overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.95))',
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    opacity: 0,
                    transition: 'opacity 0.4s ease',
                    zIndex: 2
                  }}
                >
                  <h3 style={{
                    margin: "0px 0px 10px 0px",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "clamp(22px, 4vw, 32px)",
                    fontWeight: 600,
                    lineHeight: "1.2em",
                    color: "white",
                    letterSpacing: "-0.025em"
                  }}>
                    {member.name}
                  </h3>
                  <p style={{
                    margin: "0px 0px 8px 0px",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "clamp(16px, 2.5vw, 18px)",
                    fontWeight: 500,
                    color: "rgba(255, 255, 255, 0.9)"
                  }}>
                    {member.position}
                  </p>
                  <p style={{
                    margin: "0px 0px 15px 0px",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "clamp(14px, 2vw, 16px)",
                    fontWeight: 400,
                    color: "rgba(255, 255, 255, 0.8)"
                  }}>
                    {typeof member.specialization === 'string' && member.specialization.includes('<') 
                      ? member.specialization.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').trim()
                      : member.specialization}
                  </p>
                  <p style={{
                    margin: "0px 0px 20px 0px",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "clamp(12px, 2vw, 14px)",
                    fontWeight: 400,
                    lineHeight: "1.5em",
                    color: "rgba(255, 255, 255, 0.9)"
                  }}>
                    {member.bio ? member.bio.substring(0, 120) + '...' : 'Professional legal expertise...'}
                  </p>
                  <div style={{
                    padding: '8px 16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'white',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Click for Full Profile
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        /* Animations for members section */
        @keyframes fadeIn {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }

        @keyframes slideInUp {
          0% {
            opacity: 0;
            transform: translateY(40px) scale(0.9);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        /* Enhanced hover effects for member cards */
        .member-card:hover .member-name {
          transform: translateY(-5px) scale(1.05) !important;
          text-shadow: 0 4px 16px rgba(0, 0, 0, 0.9), 0 0 20px rgba(255, 255, 255, 0.3) !important;
          letter-spacing: 0.02em !important;
          animation: nameGlow 0.6s ease-out !important;
        }

        .member-card:hover .member-position {
          transform: translateY(-3px) !important;
          opacity: 0.95 !important;
          text-shadow: 0 3px 12px rgba(0, 0, 0, 0.8) !important;
        }

        .member-card:hover .member-specialization {
          transform: translateY(-2px) !important;
          opacity: 0.9 !important;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.8) !important;
        }

        @keyframes nameGlow {
          0% {
            text-shadow: 0 2px 8px rgba(0, 0, 0, 0.8);
          }
          50% {
            text-shadow: 0 4px 16px rgba(0, 0, 0, 0.9), 0 0 30px rgba(255, 255, 255, 0.4);
          }
          100% {
            text-shadow: 0 4px 16px rgba(0, 0, 0, 0.9), 0 0 20px rgba(255, 255, 255, 0.3);
          }
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          /* Modal Container Mobile Full Screen */
          div[style*="position: fixed"][style*="100vw"] {
            padding: 0 !important;
          }
          
          div[style*="maxWidth: 950px"] {
            border-radius: 0 !important;
            max-width: 100% !important;
            width: 100% !important;
            height: 100vh !important;
            max-height: 100vh !important;
            margin: 0 !important;
          }
          
          /* Grid Layout Fixes */
          .members-grid {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
            padding: 0 10px !important;
          }
          
          /* Member Card Fixes */
          .member-card {
            height: 260px !important;
            margin: 0 auto !important;
            max-width: 320px !important;
            width: 100% !important;
          }
          
          /* Disable hover effects on mobile */
          .member-card:hover {
            transform: none !important;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
          }
          
          .member-overlay {
            display: none !important;
          }
          
          /* Profile Modal Fixes */
          .profile-modal {
            padding: 0 !important;
            width: 100% !important;
            height: 100vh !important;
            overflow-y: auto !important;
            max-height: 100vh !important;
          }
          
          .profile-modal > div {
            padding: 20px 15px !important;
            padding-top: 60px !important;
          }
          
          /* Profile Header Mobile Layout */
          .profile-header {
            flex-direction: column !important;
            text-align: center !important;
            gap: 15px !important;
            margin-bottom: 20px !important;
            padding: 20px 15px !important;
            background: none !important;
            border: none !important;
            box-shadow: none !important;
          }
          
          .profile-image {
            width: 100px !important;
            height: 100px !important;
            margin: 0 auto !important;
            border-radius: 50% !important;
          }
          
          /* Text Sizing for Mobile */
          .profile-modal h2 {
            font-size: clamp(22px, 5vw, 28px) !important;
            text-align: center !important;
          }
          
          .profile-modal h3 {
            font-size: 18px !important;
            margin-bottom: 10px !important;
          }
          
          .profile-modal h4 {
            font-size: 16px !important;
            margin-bottom: 8px !important;
          }
          
          .profile-modal p {
            font-size: 14px !important;
            line-height: 1.5 !important;
          }
          
          .profile-modal ul li {
            font-size: 13px !important;
          }
          
          /* Bio Section Mobile */
          .profile-modal div[style*="marginBottom: 40px"] {
            margin-bottom: 25px !important;
            padding: 20px 15px !important;
            border-radius: 12px !important;
          }
          
          /* Professional Details Grid Mobile */
          .profile-modal div[style*="grid-template-columns"] {
            grid-template-columns: 1fr !important;
            gap: 15px !important;
            padding: 0 10px !important;
          }
          
          .profile-modal div[style*="padding: 24px"] {
            padding: 15px !important;
            border-radius: 12px !important;
          }
          
          /* Action Button Mobile */
          .profile-modal div[style*="justify-content: center"] {
            padding-top: 15px !important;
          }
          
          .profile-modal button {
            width: 100% !important;
            max-width: 280px !important;
            text-align: center !important;
            justify-content: center !important;
            padding: 14px 20px !important;
            font-size: 13px !important;
          }
          
          /* Position Badge Mobile */
          .profile-modal span[style*="padding: 6px 12px"][style*="textTransform: uppercase"] {
            font-size: 10px !important;
            padding: 4px 8px !important;
            margin-bottom: 10px !important;
          }
          
          /* Languages Tags Mobile */
          .profile-modal span[style*="padding: 8px 14px"] {
            font-size: 11px !important;
            padding: 6px 10px !important;
          }
          
          /* Close Button Mobile - Corner Position */
          .modal-close-button {
            top: 12px !important;
            right: 12px !important;
            width: 36px !important;
            height: 36px !important;
            position: fixed !important;
            z-index: 999999 !important;
          }
          
          /* Section Header Mobile */
          section div[style*="textAlign: center"] h2 {
            font-size: clamp(28px, 7vw, 40px) !important;
            margin-bottom: 12px !important;
          }
          
          section div[style*="textAlign: center"] p {
            font-size: 16px !important;
            padding: 0 15px !important;
          }
        }
        
        /* Tablet responsiveness */
        @media (max-width: 1024px) and (min-width: 769px) {
          .members-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 20px !important;
          }
          
          .member-card {
            height: 300px !important;
          }
          
          .profile-modal {
            width: 90% !important;
            padding: 30px !important;
          }
        }
      `}</style>
    </>
  )
}

export default MembersSection
